export interface RegressionResult {
  slope: number;
  intercept: number;
  r2: number;
  predict: (t: number) => number;
}

/**
 * Ordinary least-squares linear regression.
 * @param points Array of [x, y] pairs where x is a numeric timestamp (ms)
 * @returns RegressionResult or null if insufficient data (< 2 points)
 */
export function linearRegression(
  points: [number, number][]
): RegressionResult | null {
  const n = points.length;
  if (n < 2) return null;

  let sumX = 0, sumY = 0, sumXX = 0, sumXY = 0;
  for (const [x, y] of points) {
    sumX += x;
    sumY += y;
    sumXX += x * x;
    sumXY += x * y;
  }

  const denom = n * sumXX - sumX * sumX;
  if (denom === 0) return null;

  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;

  const meanY = sumY / n;
  let ssTot = 0, ssRes = 0;
  for (const [x, y] of points) {
    ssTot += (y - meanY) ** 2;
    ssRes += (y - (slope * x + intercept)) ** 2;
  }
  const r2 = ssTot === 0 ? 1 : 1 - ssRes / ssTot;

  return {
    slope,
    intercept,
    r2,
    predict: (t: number) => slope * t + intercept,
  };
}
